import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  viewChild,
  inject,
} from '@angular/core';
import { addDays, startOfDay } from 'date-fns';
import {
  ModalComponent,
  ModalRef,
  BackendResponse,
  RadioRowV2Option,
  ToastV2Service,
  UserRightsService,
  ModalModule,
  IconV2Module,
  RadioV2Module,
  InputV2Module,
  DateV2Module,
  ButtonV2Module,
} from '@schenck/rotec-components';
import { TaskBase } from 'src/api';
import {
  TaskModalMainHeaderItem,
  TaskModalMainComponent,
} from '../task-modal-main/task-modal-main.component';
import { TaskModalService } from '../task-modal.service';
import { TaskInfoDetailItem, TaskInfoComponent } from '../task-info/task-info.component';
import { UserRightsActions } from '../../../utils/enums/user-rights.enum';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { TaskDocumentsComponent } from '../task-documents/task-documents.component';

enum MachineTaskModalView {
  Done = 'Done',
  Shift = 'Shift',
  Skip = 'Skip',
}

export enum TaskModalAction {
  Done = 'Done',
  Shift = 'Shift',
  Skip = 'Skip',
  Update = 'Update',
  Delete = 'Delete',
}

@Component({
  selector: 'ma-task-modal',
  templateUrl: './task-modal.component.html',
  styleUrls: ['./task-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalModule,
    TaskModalMainComponent,
    IconV2Module,
    TaskInfoComponent,
    RadioV2Module,
    TranslatePipe,
    TranslateDirective,
    InputV2Module,
    TaskDocumentsComponent,
    DateV2Module,
    ButtonV2Module,
    AsyncPipe,
  ],
})
export class TaskModalComponent<Task extends TaskBase> implements OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalRef = inject<ModalRef<string, TaskModalAction>>(ModalRef);
  private readonly toastService = inject(ToastV2Service);
  public taskModalService = inject<TaskModalService<Task>>(TaskModalService);
  public userRightsService = inject(UserRightsService);

  public actions = UserRightsActions;
  /**
   * The task
   */
  @Input()
  public task: Task;
  /**
   * Additional task information that should be shown in the header.
   */
  @Input()
  public taskInfos: TaskModalMainHeaderItem[][];
  /**
   * Additional defatils that should be shown inside the expandable.
   */
  @Input()
  public details: TaskInfoDetailItem[][];
  /**
   * Picture that should be shown on the top right.
   */
  @Input()
  public pictureId: string;

  // for done/shift/skip
  public description = '';
  public modalTitle: string;
  public isSeries: boolean;
  public errorMsg = '';
  public readonly modal = viewChild<ModalComponent>('modal');
  // to determine which of done/shift/skip is active
  public machineTaskModalView = MachineTaskModalView;
  public view: MachineTaskModalView = MachineTaskModalView.Done;
  public options: RadioRowV2Option[] = [
    { value: MachineTaskModalView.Done, icon: 'check', name: 'Done' },
    { value: MachineTaskModalView.Shift, icon: 'rollback', name: 'Shift' },
    { value: MachineTaskModalView.Skip, icon: 'rotation-cw', name: 'Skip' },
  ];
  // for shift
  public minDate = startOfDay(addDays(new Date(), 1));
  public newDueDate = this.minDate;
  public disableConfirmBtn = false;
  private nativeDate: string;

  constructor() {
    this.taskModalService.setId(this.modalRef.data);
  }

  public ngOnChanges(): void {
    if (!this.task) {
      return;
    }

    this.newDueDate =
      this.task.dueDate.getTime() > this.minDate.getTime() ? this.task.dueDate : this.minDate;
    this.nativeDate = this.newDueDate?.toISOString();
    this.isSeries = !!this.task.scheduling;
    this.modalTitle =
      this.task.schenckId && this.task.customerId
        ? this.task.schenckId + ' / ' + this.task.customerId
        : this.task.schenckId || this.task.customerId || this.task.title;
  }

  public changeDueDate(date: Date): void {
    if (this.errorMsg === 'VALIDATION_ERROR_INVALID_TASK_ACTION_SHIFTED_DUE_DATE') {
      this.errorMsg = '';
    }
    this.newDueDate = date;
    this.nativeDate = date?.toISOString();
  }

  public onDateInputChanged(value: string): void {
    this.nativeDate = value;
  }

  public onDateLeave() {
    this.newDueDate = new Date(this.nativeDate);
  }

  public confirm(): void {
    this.disableConfirmBtn = true;
    switch (this.view) {
      case MachineTaskModalView.Done:
        return this.finish();
      case MachineTaskModalView.Shift:
        return this.shift();
      case MachineTaskModalView.Skip:
        return this.skip();
    }
  }

  private finish(): void {
    this.errorMsg = '';
    this.taskModalService
      .done$(this.description)
      .subscribe(this.reactToResponse(TaskModalAction.Done));
  }

  private shift(): void {
    this.errorMsg = '';
    this.taskModalService
      .shift$(this.description, this.newDueDate)
      .subscribe(this.reactToResponse(TaskModalAction.Shift));
  }

  private skip(): void {
    this.errorMsg = '';
    this.taskModalService
      .skip$(this.description)
      .subscribe(this.reactToResponse(TaskModalAction.Skip));
  }

  public get isConfirmBtnDisabled(): boolean {
    return (
      ((!this.newDueDate || !Date.parse(this.nativeDate)) &&
        this.view === MachineTaskModalView.Shift) ||
      this.taskModalService.taskLoading$.value ||
      !this.description.trim() ||
      this.disableConfirmBtn
    );
  }

  public openEditModal(): void {
    this.errorMsg = '';
    this.taskModalService.openEditModal$().subscribe((result) => {
      if (result) {
        this.modalRef.close();
      }
    });
  }

  public editNotes(notes: string): void {
    this.taskModalService.editNotes$(notes).subscribe();
  }

  public deleteTask(): void {
    this.errorMsg = '';
    this.taskModalService.delete$().subscribe(this.reactToResponse(TaskModalAction.Delete));
  }

  public close(): void {
    this.modalRef.close();
  }

  public setDocuments(ids: string[]): void {
    this.errorMsg = '';
    const hasAddedDocs = this.task.taskDocumentIds.length < ids.length;
    this.taskModalService.setTaskDocuments$(ids).subscribe(() => {
      if (hasAddedDocs) {
        this.scrollBottom();
      }
    });
  }

  private scrollBottom(): void {
    setTimeout(() => this.modal().setScrollTop(10000), 1000);
  }

  private emitAndClose(action: TaskModalAction): void {
    this.modalRef.emitAndClose(action);
  }

  // Ad lambda function to keep this-scope.
  private reactToResponse = (taskActionInCaseOfSuccess: TaskModalAction) => {
    return BackendResponse.reactToResponse<void>({
      success: () => this.emitAndClose(taskActionInCaseOfSuccess),
      badRequest: (msg) => this.showErrorMsg(msg),
      notFound: () => this.showErrorMsg('ERROR_NOT_FOUND_task does not exist'),
      error: () => this.showErrorMsg('TECHNICAL_ERROR_try again later or contact admin'),
    });
  };

  private showErrorMsg(msg: string): void {
    this.disableConfirmBtn = false;
    this.errorMsg = msg;
    this.toastService.openError(this.errorMsg);
    this.cdr.detectChanges();
  }
}
